import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

const QUAD_COLOR = {
  STAR:      "#166534",  // deep green
  WORKHORSE: "#a16207",  // amber
  PUZZLE:    "#1d4ed8",  // blue
  DOG:       "#7f1d1d",  // dark red
};
const QUAD_LABEL = {
  STAR:      "Star — Alto volume, alto margine",
  WORKHORSE: "Workhorse — Alto volume, margine basso",
  PUZZLE:    "Puzzle — Basso volume, alto margine",
  DOG:       "Dog — Basso volume, basso margine",
};

export default function MenuEngineering() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/profitability/menu-engineering?days=${days}`);
    setData(data);
  };
  useEffect(() => { load(); }, [days]);

  if (!data) return <div className="text-neutral-500" data-testid="me-loading">Caricamento…</div>;

  const items = data.items || [];
  const rows = items.map((x) => ({
    ...x,
    x: x.sales_count, y: x.margin_unit,
    fill: QUAD_COLOR[x.classification] || "#525252",
  }));

  return (
    <div data-testid="menu-engineering-page">
      <PageHeader eyebrow="Analisi menu" title="Menu Engineering"
        subtitle="Ogni prodotto è classificato in modo deterministico su popolarità e margine unitario." />

      <div className="flex items-center gap-2 mb-6">
        {[7, 30, 60, 90].map((d) => (
          <Button key={d} size="sm" variant={days === d ? "default" : "outline"}
            onClick={() => setDays(d)} data-testid={`days-${d}`}>
            {d} giorni
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {["STAR", "WORKHORSE", "PUZZLE", "DOG"].map((q) => (
          <div key={q} data-testid={`counts-${q}`}
               className="border border-[#E2D9C8] rounded-lg p-4 bg-white">
            <div className="text-xs uppercase tracking-wider text-neutral-500">{QUAD_LABEL[q]}</div>
            <div className="text-3xl font-serif mt-1" style={{ color: QUAD_COLOR[q] }}>
              {data.counts[q] || 0}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white p-4 mb-6" style={{ height: 380 }}>
        <ResponsiveContainer>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" dataKey="x" name="Vendite (unità)" tick={{ fontSize: 11 }}
                   label={{ value: "Popolarità (unità vendute)", position: "insideBottom", offset: -20, style: { fontSize: 12 } }} />
            <YAxis type="number" dataKey="y" name="Margine unitario €" tick={{ fontSize: 11 }}
                   label={{ value: "Margine unitario €", angle: -90, position: "insideLeft", style: { fontSize: 12 } }} />
            {rows[0] && rows[0].median_popularity !== undefined && (
              <ReferenceLine x={rows[0].median_popularity} stroke="#8B7355" strokeDasharray="4 4" />
            )}
            {rows[0] && rows[0].median_margin_unit !== undefined && (
              <ReferenceLine y={rows[0].median_margin_unit} stroke="#8B7355" strokeDasharray="4 4" />
            )}
            <Tooltip cursor={{ strokeDasharray: "3 3" }}
              formatter={(value, key) => key === "y" ? `${fmtEUR(value)}` : value}
              labelFormatter={() => ""}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="bg-white border rounded p-2 text-xs shadow" data-testid="me-tooltip">
                    <div className="font-semibold">{d.name}</div>
                    <div>Categoria: {d.category}</div>
                    <div>Vendite: {d.sales_count}</div>
                    <div>Margine unitario: {fmtEUR(d.margin_unit)}</div>
                    <div>Margine totale: {fmtEUR(d.margin_total)}</div>
                    <div>Fatturato: {fmtEUR(d.revenue)}</div>
                    <div className="mt-1"><Badge>{d.classification}</Badge></div>
                  </div>
                );
              }} />
            <Scatter data={rows} onClick={(d) => setSelected(d)}>
              {rows.map((r, i) => <Cell key={i} fill={r.fill} />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {selected && (
        <div className="border border-neutral-900 rounded-lg bg-white p-4 mb-6" data-testid="me-selected">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs uppercase text-neutral-500">Selezionato · {selected.classification}</div>
              <h3 className="font-serif text-2xl">{selected.name}</h3>
            </div>
            <Button size="sm" variant="outline" onClick={() => setSelected(null)}>chiudi</Button>
          </div>
          <p className="text-sm mt-2">{(data.recommendations[selected.classification] || {}).priority}</p>
          <ul className="list-disc list-inside text-sm mt-2 text-neutral-700">
            {(data.recommendations[selected.classification]?.actions || []).map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Prodotto</th>
            <th className="p-3">Categoria</th>
            <th className="p-3">Classe</th>
            <th className="p-3 text-right">Vendite</th>
            <th className="p-3 text-right">Prezzo</th>
            <th className="p-3 text-right">Margine unit.</th>
            <th className="p-3 text-right">Margine totale</th>
            <th className="p-3 text-right">Fatturato</th>
            <th className="p-3">Confidenza</th>
          </tr></thead>
          <tbody data-testid="me-tbody">
            {items.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-neutral-400">
                Nessun dato — servono vendite negli ultimi {days} giorni.
              </td></tr>
            )}
            {items.map((x) => (
              <tr key={x.menu_item_id} className="border-t border-[#E2D9C8] hover:bg-[#FDFBF7] cursor-pointer"
                  onClick={() => setSelected(x)} data-testid={`me-row-${x.menu_item_id}`}>
                <td className="p-3">{x.name}</td>
                <td className="p-3 text-xs text-neutral-500">{x.category || "—"}</td>
                <td className="p-3">
                  <span className="px-2 py-0.5 rounded text-xs text-white" style={{ background: QUAD_COLOR[x.classification] }}>
                    {x.classification}
                  </span>
                </td>
                <td className="p-3 text-right">{x.sales_count}</td>
                <td className="p-3 text-right">{fmtEUR(x.sale_price)}</td>
                <td className="p-3 text-right">{fmtEUR(x.margin_unit)}</td>
                <td className="p-3 text-right">{fmtEUR(x.margin_total)}</td>
                <td className="p-3 text-right">{fmtEUR(x.revenue)}</td>
                <td className="p-3 text-xs">
                  <span className={`px-2 py-0.5 rounded ${
                    x.cogs_confidence === "actual" ? "bg-emerald-100 text-emerald-800" :
                    x.cogs_confidence === "estimated" ? "bg-amber-100 text-amber-800" :
                    "bg-neutral-200 text-neutral-600"
                  }`}>
                    {x.cogs_confidence === "actual" ? "reale" :
                     x.cogs_confidence === "estimated" ? "stima" : "dati insuff."}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
